import {
  isJourneySnapshot,
  canonicalQuoteOrigin,
  parseJourneyPointer,
  type JourneySnapshot,
} from "./quoteJourneyState";
import type { createQuoteJourneyStore } from "./quoteJourneyStore";
export const JOURNEY_POINTER_KEY = "cnc.quickquote.journey.v1";
export type JourneyStorage = Pick<
  Storage,
  "getItem" | "setItem" | "removeItem"
>;
export type JourneyRepository = ReturnType<typeof createQuoteJourneyStore>;
export type JourneyPersistence = {
  store: JourneyRepository;
  storage: JourneyStorage;
  now?: number;
};
export async function stageQuoteJourney(
  snapshot: JourneySnapshot,
  deps: JourneyPersistence,
): Promise<void> {
  if (!isJourneySnapshot(snapshot, snapshot?.scope, deps.now ?? Date.now()))
    throw new Error("报价暂存信息无效，请留在这里重试");
  const { id, scope } = snapshot;
  let previous: string | null;
  try {
    previous = parseJourneyPointer(
      deps.storage.getItem(JOURNEY_POINTER_KEY),
      scope,
    );
  } catch {
    throw new Error("浏览器暂时不能保存返回位置，请留在这里重试");
  }
  if (previous === id) throw new Error("返回位置已使用，请重新打开设置");
  try {
    await deps.store.save(snapshot);
  } catch {
    throw new Error("未能暂存当前报价，请留在这里重试");
  }
  try {
    deps.storage.setItem(
      JOURNEY_POINTER_KEY,
      JSON.stringify({ version: 1, scope, id }),
    );
  } catch {
    try {
      await deps.store.delete(id);
    } catch {
      throw new Error("本地暂存未能清理，请留在这里重试");
    }
    throw new Error("浏览器暂时不能保存返回位置，请留在这里重试");
  }
}
export async function restoreQuoteJourney(
  routeId: unknown,
  scope: string,
  deps: JourneyPersistence,
): Promise<JourneySnapshot | null> {
  if (typeof routeId !== "string") return null;
  try {
    const id = parseJourneyPointer(
      deps.storage.getItem(JOURNEY_POINTER_KEY),
      scope,
    );
    if (!id || id !== routeId) return null;
    const now = deps.now ?? Date.now();
    const snapshot = await deps.store.load(id, scope, now);
    const currentId = parseJourneyPointer(
      deps.storage.getItem(JOURNEY_POINTER_KEY),
      scope,
    );
    if (
      currentId !== id ||
      !isJourneySnapshot(snapshot, scope, deps.now ?? Date.now()) ||
      snapshot.id !== id
    )
      return null;
    const origin = canonicalQuoteOrigin(snapshot.origin);
    return origin && origin !== snapshot.origin
      ? { ...snapshot, origin }
      : snapshot;
  } catch {
    throw new Error("暂时没能恢复原报价，请留在这里重试");
  }
}
